import { createSignal } from 'solid-js';
import { createMagicDoorContext } from '~/contexts/utils';
import { PolicyHandbookRepository } from '~/repositories/policyHandbookRepository';
import type { CreatePolicyHandbookDto, PolicyHandbookDto, UpdatePolicyHandbookDto } from '~/swagger/portal/Api';

export const [PolicyHandbookProvider, usePolicyHandbook] = createMagicDoorContext('PolicyHandbook', () => {
  const repo = new PolicyHandbookRepository();

  const [isLoading, setIsLoading] = createSignal(false);
  const [error, setError] = createSignal<Error | undefined>(undefined);
  const [policyHandbooks, setPolicyHandbooks] = createSignal<PolicyHandbookDto[]>([]);

  const getPolicyHandbooks = async () => {
    try {
      setIsLoading(true);
      const result = await repo.getPolicyHandbooks();
      setPolicyHandbooks(result.items || []);
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setIsLoading(false);
    }
  };

  const createPolicyHandbook = async (payload: CreatePolicyHandbookDto) => {
    try {
      setIsLoading(true);
      await repo.createPolicyHandbook(payload);
      await getPolicyHandbooks();
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const updatePolicyHandbook = async (id: string, payload: UpdatePolicyHandbookDto) => {
    try {
      setIsLoading(true);
      await repo.updatePolicyHandbook(id, payload);
      await getPolicyHandbooks();
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const deletePolicyHandbook = async (id: string) => {
    try {
      setIsLoading(true);
      await repo.deletePolicyHandbook(id);
      await getPolicyHandbooks();
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    error,
    isLoading,
    policyHandbooks,
    getPolicyHandbooks,
    createPolicyHandbook,
    updatePolicyHandbook,
    deletePolicyHandbook,
  };
});
